// ── ARC-CO vs PLC — ONE copy of the arithmetic ───────────────────────────────
//
// This formula existed twice: `arcPlcAcres`/`arcPlcRec` inside
// CoverageAdvisorView, and `caArcPlcCountyRec` at module scope for the Full
// Strategy Report — whose own comment read *"MIRRORS
// CoverageAdvisorView.arcPlcRec (keep the formula in sync)"*.
//
// A rule kept in step by hand is a rule that drifts, and this repo has paid for
// that three times over (two guarantee engines, three news handlers, three
// normFarmerName copies). A REPORT is the worst place to keep the second copy:
// it is the document the grower actually holds, so the one that drifts is the
// one that leaves the building.
//
// The closure could not be shared because it captures the Coverage Advisor's
// state. So the ARITHMETIC — the part that was duplicated — lives here, and both
// callers keep their own data plumbing.
//
// ── SOURCED CONSTANTS ────────────────────────────────────────────────────────
//
// ⚠ THE ARC-CO CAP IS 12% OF THE BENCHMARK REVENUE. FSA's own fact sheet says
// 10% and is WRONG — this repo already records that (see the ARC cap memo). Do
// not "correct" it back.
export const ARC_CAP_FRAC = 0.12;

// ⚠ THE CAP IS A PUBLISHED RATE, TO THE CENT — FSA's county file carries it as its own column,
// "2026 Maximum Payment Rate (12% of C)", stored to two decimals (Livingston NY corn: $120.85
// against 12% × $1,007.11 = $120.8532), and the payment rate FSA pays is "Lesser of E or I" —
// that published cent figure. Payments are then that RATE × 85% × base acres, so a per-base-acre
// maximum is only traceable to FSA when it is built from the cent rate: $120.85 × 85% = $102.72,
// where the unrounded 12% printed $102.73. Found reconciling the Farm Election Summary against
// FSA's own file (2026-09-19). One rule, every caller.
export function arcCapRate(benchmarkRevenue) {
  const b = num(benchmarkRevenue);
  return b != null ? Math.round(ARC_CAP_FRAC * b * 100) / 100 : null;
}
// Payments are made on 85% of base acres, for both programs.
export const BASE_ACRE_FACTOR = 0.85;
// PLC wins a near-tie: the $0.50 is a display tie-break so a rounding difference
// does not flip the recommendation between two renders of the same numbers.
export const PICK_TIEBREAK = 0.5;

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

// PLC $/base acre. Scales with the FARM's FSA payment yield, which is why an
// assumed yield must always be labelled by the caller.
export function plcPerBaseAcre(plcRate, paymentYield) {
  const r = num(plcRate), y = num(paymentYield);
  return (r != null && y != null && y > 0) ? r * y * BASE_ACRE_FACTOR : null;
}

// ⚠ THE PROGRAM PRICE IS THE HIGHER OF THE MYA AND THE NATIONAL LOAN RATE (FSA's 2026
// formulas; Kevin, 2026-09-19). PLC pays the effective reference price LESS that price, and
// ARC-CO's actual revenue is the county yield TIMES it — so once the MYA falls below the loan
// rate neither payment moves again. This is the ONE place the floor is applied; countyRead,
// electionOutlook and plcPaysMoreBelow all take their price through it, and no renderer floors
// anything. A loan rate that is not known cannot be applied, so the price stays the MYA — the
// only behaviour this engine had before.
export function programPrice(myaPrice, loanRate) {
  const m = num(myaPrice), l = num(loanRate);
  if (m == null) return null;
  return l != null && l > m ? l : m;
}

// The PLC payment rate, from the reference price and the program price when not published.
export function plcRateFrom({ plcPaymentRate, effectiveRefPrice, myaPrice, loanRate = null }) {
  const p = num(plcPaymentRate);
  if (p != null) return p;
  const erp = num(effectiveRefPrice), pp = programPrice(myaPrice, loanRate);
  return (erp != null && pp != null) ? Math.max(0, erp - pp) : null;
}

// ARC-CO payment RATE per acre: the revenue shortfall, capped at 12% of the
// benchmark. Returns null when either side is unknown — never zero, because
// "no shortfall" and "we could not compute it" are different answers.
export function arcRatePerAcre({ guaranteeRevenue, actualRevenue, benchmarkRevenue }) {
  const g = num(guaranteeRevenue), a = num(actualRevenue), b = num(benchmarkRevenue);
  if (g == null || a == null || b == null) return null;
  return Math.min(Math.max(0, g - a), arcCapRate(b));
}

export function arcPerBaseAcre(arcRate) {
  const r = num(arcRate);
  return r != null ? r * BASE_ACRE_FACTOR : null;
}

// The pick. Null when either side is unknown — a missing figure does not hand
// the election to the other program by default.
export function arcPlcPick(plcAc, arcAc) {
  const p = num(plcAc), a = num(arcAc);
  if (p == null || a == null) return null;
  return (p + PICK_TIEBREAK >= a) ? "PLC" : "ARC-CO";
}

// The whole county economics in one call, for a caller that has the raw row.
// `actualRevenue` may be supplied directly or derived from yield × MYA.
export function arcPlcCountyEconomics({
  effectiveRefPrice, myaPrice, plcPaymentRate, paymentYield,
  benchmarkYield, actualYield, actualRevenue, benchmarkRevenue, guaranteeRevenue, loanRate = null,
}) {
  const mya = programPrice(myaPrice, loanRate);
  const plcRate = plcRateFrom({ plcPaymentRate, effectiveRefPrice, myaPrice, loanRate });
  const bYld = num(benchmarkYield);
  const aYld = num(actualYield) != null ? num(actualYield) : bYld;
  const aRev = num(actualRevenue) != null ? num(actualRevenue)
    : (aYld != null && mya != null ? aYld * mya : null);
  const arcRate = arcRatePerAcre({ guaranteeRevenue, actualRevenue: aRev, benchmarkRevenue });
  const arcAc = arcPerBaseAcre(arcRate);
  const plcAc = plcPerBaseAcre(plcRate, paymentYield);
  return { plcRate, plcAc, arcRate, arcAc, pick: arcPlcPick(plcAc, arcAc),
           actualRevenueUsed: aRev, capApplied: arcRate != null && benchmarkRevenue != null
             && Math.abs(arcRate - arcCapRate(benchmarkRevenue)) < 1e-9 };
}

// ⚠ ARC AND SCO ARE NO LONGER MUTUALLY EXCLUSIVE. The One Big Beautiful Bill Act
// amended the SCO endorsement to be purchasable regardless of the insured's FSA
// ARC election (RMA MGR-25-006). Any surface still applying the old rule is
// wrong, and `checkConsistency` blocks a strategy that does.
export const ARC_BLOCKS_SCO = false;
